import { HierarchicalBulkTimeEntry } from '@totem/components/ticketing/details/timeEntry/bulkTimeEntry/types';
import { TICKETING_FILTER_DESCRIPTORS } from '@totem/components/ticketing/filterDescriptors';
import { Optimize } from '@totem/components/ticketing/optimizeTypes';
import {
  Activity,
  Duration,
  HierarchicalTask,
  MIN_DATE_TIME_STRING,
  Task,
  TASK_TYPE_MILESTONE,
  TaskWorkflowSteps,
  Ticket,
  TicketingConnectionInput,
  TicketingFilters,
  TicketQueryResult,
} from '@totem/components/ticketing/ticketing';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import { Filter } from '@totem/types/common';
import { isNotNull, isNull } from '@totem/utilities/common';
import { buildFiltersFromDescriptors } from '@totem/utilities/filterUtilities';
import {
  sortNumberAscending,
  sortNumberDescending,
} from '@totem/utilities/tableUtilities';

export const defaultTicket: Ticket = {
  activity: [],
  assignments: [],
  buildingId: emptyId,
  closedAt: MIN_DATE_TIME_STRING,
  resolvedAt: MIN_DATE_TIME_STRING,
  controlSystemId: emptyId,
  createdAt: MIN_DATE_TIME_STRING,
  createdById: emptyId,
  createdByName: '',
  createdByType: '',
  description: '',
  deviceId: emptyId,
  keys: [],
  partiesInvolved: [],
  impact: '',
  urgency: '',
  priority: '',
  queueAssignments: [],
  regionId: emptyId,
  status: 'OPEN',
  subStatus: '',
  summary: '',
  type: 'Service Request',
  category: '',
  subCategory: '',
  updatedAt: MIN_DATE_TIME_STRING,
  id: emptyId,
  number: '',
  organizationId: emptyId,
  attributes: [],
  actualHours: 0.0,
  tasks: [],
  timeEntries: [],
  billingEntries: [],
  approvalRequests: [],
  project: null,
  followUpAt: null,
  internalOnly: false,
  extras: {
    expenses: [],
    customerInfo: {
      whatItMeans: '',
      whyTicketRaised: '',
      impact: '',
    },
    technicianInfo: {
      likelyCauses: [],
      actions: [],
    },
  },
  relatedTickets: [],
  favorites: [],
};

export const defaultActivity: Activity = {
  id: '',
  type: {
    id: '',
    code: '',
    display: '',
  },
  notes: {
    isRichText: true,
    plainText: '',
    htmlText: '',
  },
  attributes: [],
  createdByType: 'User',
  createdByName: '',
  createdById: '',
  createdAt: new Date().toISOString(),
  isPrivate: false,
  keys: [],
};

export const defaultOptimize: Optimize = {
  savingsType: 'Potential Savings',
  savingsPotential: 0,
  savingsTerm: 0,
  savingsTermPeriod: '',
  category: '',
  requiredInvestment: 0,
  stepsRequired: '',
  levelOfEffort: '',
  scopeOfReturn: '',
  implementationDate: '',
  opportunityStatus: 'Draft',
  verifiedAnnualSavings: 0,
  deferredUntil: MIN_DATE_TIME_STRING,
  statusHistory: [],
};

export const buildFilters = (input: TicketingConnectionInput): Filter[] =>
  buildFiltersFromDescriptors(input, TICKETING_FILTER_DESCRIPTORS);

export const addFilters = (
  currentFilters: Filter[],
  input: TicketingFilters | undefined,
): Filter[] => {
  if (!input) {
    return currentFilters;
  }
  return [
    ...currentFilters,
    ...buildFiltersFromDescriptors(input, TICKETING_FILTER_DESCRIPTORS),
  ];
};

export const formatDuration = (duration: Duration) => {
  let durationOut = '';

  if (duration.days > 0) {
    if (durationOut !== '') {
      durationOut += ' ';
    }
    durationOut += `${duration.days}d`;
  }
  if (duration.hours > 0) {
    if (durationOut !== '') {
      durationOut += ' ';
    }
    durationOut += `${duration.hours}h`;
  }
  if (duration.minutes > 0) {
    if (durationOut !== '') {
      durationOut += ' ';
    }
    durationOut += `${duration.minutes}m`;
  }

  return durationOut;
};

export const getDefaultTicketTypes = () => {
  return ['Service Request', 'Change Request', 'Incident', 'Problem'];
};

export const getDefaultTaskWorkflowSteps = (): TaskWorkflowSteps => {
  return [
    {
      code: 'PENDING',
      name: 'Pending',
      description: 'Task has been created but not yet started.',
      options: ['SCHEDULED', 'IN PROGRESS', 'CANCELLED'],
    },
    {
      code: 'SCHEDULED',
      name: 'Scheduled',
      description: 'Task scheduled for future execution.',
      options: ['IN PROGRESS', 'ON HOLD', 'CANCELLED'],
    },
    {
      code: 'IN PROGRESS',
      name: 'In Progress',
      description: 'Work has started on the task.',
      options: [
        'SCHEDULED',
        'WAITING',
        'ON HOLD',
        'REVIEW',
        'COMPLETED',
        'CANCELLED',
      ],
    },
    {
      code: 'WAITING',
      name: 'Waiting',
      description:
        'Blocked, waiting on external dependencies (e.g. another task, external input).',
      options: ['SCHEDULED', 'IN PROGRESS', 'ON HOLD', 'CANCELLED'],
    },
    {
      code: 'ON HOLD',
      name: 'On Hold',
      description: 'Work paused temporarily.',
      options: ['SCHEDULED', 'WAITING', 'IN PROGRESS', 'CANCELLED'],
    },
    {
      code: 'REVIEW',
      name: 'In Review',
      description: 'Work completed, pending review/approval.',
      options: ['APPROVED', 'REJECTED', 'COMPLETED', 'CANCELLED'],
    },
    {
      code: 'APPROVED',
      name: 'Approved',
      description: 'Review completed successfully.',
      options: ['COMPLETED'],
    },
    {
      code: 'REJECTED',
      name: 'Rejected',
      description: 'Review failed; needs revision.',
      options: ['SCHEDULED', 'IN PROGRESS', 'CANCELLED'],
    },
    {
      code: 'COMPLETED',
      name: 'Completed',
      description: 'Task fully done.',
      options: ['REOPENED'],
    },
    {
      code: 'CANCELLED',
      name: 'Cancelled',
      description: 'Task was cancelled (de-scoped or no longer needed).',
      options: ['REOPENED'],
    },
    {
      code: 'REOPENED',
      name: 'Reopened',
      description: 'Was completed but reopened due to issues.',
      options: ['SCHEDULED', 'IN PROGRESS', 'CANCELLED'],
    },
  ];
};

export const sortTasksForUIDisplay = (tasks: Task[]): Task[] => {
  if (!tasks || tasks.length === 0) {
    return [];
  }

  // 1. Create a map for quick access to tasks by their 'number' field
  // This is crucial because 'parent' refers to 'number'.
  const taskNumberMap = new Map<number, Task>();
  tasks.forEach((task) => taskNumberMap.set(task.number, task));

  // 2. Identify root tasks (where parent is 0)
  const rootTasks = tasks.filter((task) => task.parent === 0);

  // 3. Sort root tasks by their position
  rootTasks.sort((compA, compB) => compA.position - compB.position);

  // 4. Recursive function to get children and sort them
  // Now, parentId refers to the 'number' of the parent task.
  const getSortedChildren = (parentTaskNumber: number): Task[] => {
    const children = tasks.filter((task) => task.parent === parentTaskNumber);
    children.sort((compA, compB) => compA.position - compB.position); // Sort siblings by position
    return children;
  };

  // 5. Flatten the hierarchy into the display order
  const displayOrder: Task[] = [];

  const traverseAndAdd = (currentTasks: Task[]) => {
    for (const task of currentTasks) {
      displayOrder.push(task); // Add the current task
      // Get children whose parent field matches the current task's 'number'
      const children = getSortedChildren(task.number);
      if (children.length > 0) {
        traverseAndAdd(children); // Recursively add children
      }
    }
  };

  traverseAndAdd(rootTasks); // Start the traversal from the sorted root tasks

  return displayOrder;
};

export const getTaskDepth = (
  taskNumber: number,
  allTasks: Task[],
  depth: number = 0,
): number => {
  const task = allTasks.find((tsk) => tsk.number === taskNumber);
  if (!task || task.parent === 0) {
    return depth;
  }
  return getTaskDepth(task.parent, allTasks, depth + 1);
};

export const buildTaskHierarchy = (flatTasks: Task[]): HierarchicalTask[] => {
  if (!flatTasks || flatTasks.length === 0) {
    return [];
  }

  // Map to quickly find tasks by their 'number' (which 'parent' refers to)
  const taskMap = new Map<number, HierarchicalTask>();

  // Initialize the map and clone tasks to prevent modifying original objects if undesired
  flatTasks.forEach((task) => {
    taskMap.set(task.number, { ...task, children: [] });
  });

  const rootTasks: HierarchicalTask[] = [];

  // Iterate through all tasks to assign children
  taskMap.forEach((task) => {
    if (task.parent === 0) {
      // This is a root task
      rootTasks.push(task);
    } else {
      // This is a child task, find its parent
      const parentTask = taskMap.get(task.parent);
      if (parentTask) {
        // Add this task to its parent's children array
        if (!parentTask.children) {
          parentTask.children = []; // Ensure children array exists
        }
        parentTask.children.push(task);
      } else {
        // Handle orphaned tasks (e.g., parent ID not found).
        // For robustness, you might log this or treat as a root.
        // eslint-disable-next-line no-console
        console.warn(
          `Task with number ${task.number} has parent ${task.parent} which was not found. Treating as root.`,
        );

        rootTasks.push(task);
      }
    }
  });

  // Sort children arrays for each task in the hierarchy
  // This needs to be done recursively or by traversing the built tree.
  const sortChildrenRecursively = (tasksToSort: HierarchicalTask[]) => {
    tasksToSort.forEach((task) => {
      if (task.children && task.children.length > 0) {
        task.children.sort((compA, compB) => compA.position - compB.position);

        sortChildrenRecursively(task.children); // Recurse for nested children
      }
    });
  };

  // Sort the top-level root tasks
  rootTasks.sort((compA, compB) => compA.position - compB.position);

  // Sort all children arrays in the entire hierarchy
  sortChildrenRecursively(rootTasks);

  return rootTasks;
};

export const buildTaskTimeEntryHierarchy = (
  flatTasks: Task[],
): HierarchicalBulkTimeEntry[] => {
  if (!flatTasks || flatTasks.length === 0) {
    return [];
  }

  // Map to quickly find tasks by their 'number' (which 'parent' refers to)
  const taskMap = new Map<number, HierarchicalBulkTimeEntry>();

  // Initialize the map and clone tasks to prevent modifying original objects if undesired
  flatTasks.forEach((task) => {
    taskMap.set(task.number, {
      ...task,
      children: [],
      timeEntry: {
        hoursLogged: 0,
        billable: false,
        description: '',
      },
    });
  });

  const rootTasks: HierarchicalBulkTimeEntry[] = [];

  // Iterate through all tasks to assign children
  taskMap.forEach((task) => {
    if (task.parent === 0) {
      // This is a root task
      rootTasks.push(task);
    } else {
      // This is a child task, find its parent
      const parentTask = taskMap.get(task.parent);
      if (parentTask) {
        // Add this task to its parent's children array
        if (!parentTask.children) {
          parentTask.children = []; // Ensure children array exists
        }
        parentTask.children.push(task);
      } else {
        // Handle orphaned tasks (e.g., parent ID not found).
        // For robustness, you might log this or treat as a root.
        // eslint-disable-next-line no-console
        console.warn(
          `Task with number ${task.number} has parent ${task.parent} which was not found. Treating as root.`,
        );

        rootTasks.push(task);
      }
    }
  });

  // Sort children arrays for each task in the hierarchy
  // This needs to be done recursively or by traversing the built tree.
  const sortChildrenRecursively = (
    tasksToSort: HierarchicalBulkTimeEntry[],
  ) => {
    tasksToSort.forEach((task) => {
      if (task.children && task.children.length > 0) {
        task.children.sort((compA, compB) => compA.position - compB.position);

        sortChildrenRecursively(task.children); // Recurse for nested children
      }
    });
  };

  // Sort the top-level root tasks
  rootTasks.sort((compA, compB) => compA.position - compB.position);

  // Sort all children arrays in the entire hierarchy
  sortChildrenRecursively(rootTasks);

  return rootTasks;
};

export const createTaskMap = (
  flatTasks: Task[],
): Map<number, HierarchicalTask> => {
  const taskMap = new Map<number, HierarchicalTask>();
  flatTasks.forEach((task) => {
    // Create a shallow copy and initialize children array
    taskMap.set(task.number, { ...task, children: [] });
  });
  return taskMap;
};

export const getPreviousTask = (
  currentTaskNumber: number,
  taskMap: Map<number, HierarchicalTask>,
  tasks: Task[],
): Task | null => {
  const currentTask = taskMap.get(currentTaskNumber);
  if (!currentTask) {
    return null; // Task not found
  }

  const parentTaskNumber = currentTask.parent;
  const siblings = tasks.filter((chk) => chk.parent === currentTask.parent);
  let parent: HierarchicalTask | undefined;

  if (parentTaskNumber === 0) {
    parent = undefined; // No explicit parent for root tasks (parent is 0)
  } else {
    parent = taskMap.get(parentTaskNumber);
  }

  if (siblings.length > 0) {
    const lowerSiblings = siblings
      .filter((chk) => chk.position < currentTask.position)
      .sort((compA, compB) =>
        sortNumberDescending(compA.position, compB.position),
      );
    if (lowerSiblings.length > 0) {
      const lowestSibling = lowerSiblings[0];
      const lowestSiblingsChildren = tasks
        .filter((chk) => chk.parent === lowestSibling.number)
        .sort((compA, compB) =>
          sortNumberDescending(compA.position, compB.position),
        );
      if (lowestSiblingsChildren.length > 0) {
        return lowestSiblingsChildren[0];
      }
      return lowerSiblings[0];
    }
  }

  return parent;
};

export const getNextTask = (
  currentTaskNumber: number,
  taskMap: Map<number, HierarchicalTask>,
  tasks: Task[],
): Task | null => {
  const currentTask = taskMap.get(currentTaskNumber);
  if (!currentTask) {
    return null; // Task not found
  }

  const children = tasks
    .filter((chk) => chk.parent === currentTask.number)
    .sort((compA, compB) =>
      sortNumberAscending(compA.position, compB.position),
    );
  if (children.length > 0) {
    return children[0];
  }

  const siblings = tasks.filter((chk) => chk.parent === currentTask.parent);
  if (siblings.length > 0) {
    const higherSiblings = siblings
      .filter((chk) => chk.position > currentTask.position)
      .sort((compA, compB) =>
        sortNumberAscending(compA.position, compB.position),
      );
    if (higherSiblings.length > 0) {
      return higherSiblings[0];
    }
  }

  if (currentTask.parent > 0) {
    const parentTask = taskMap.get(currentTask.parent);
    const parentSiblings = tasks.filter(
      (chk) => chk.parent === parentTask.parent,
    );
    if (parentSiblings.length > 0) {
      const higherParentSiblings = parentSiblings
        .filter((chk) => chk.position > parentTask.position)
        .sort((compA, compB) =>
          sortNumberDescending(compA.position, compB.position),
        );
      if (higherParentSiblings.length > 0) {
        return higherParentSiblings[0];
      }
    }
  }
  return null;
};

export const getNextRootPosition = (ticket: TicketQueryResult): number => {
  let nextPosition = 0;

  if (isNotNull(ticket) && isNotNull(ticket.ticket.tasks)) {
    ticket.ticket.tasks.forEach((task) => {
      if (task.parent === 0 && task.position >= nextPosition) {
        nextPosition = task.position + 1;
      }
    });
  }

  return nextPosition;
};

export const canMoveUp = (previousTask: HierarchicalTask): boolean => {
  if (isNull(previousTask)) {
    return false;
  }
  return true;
};

export const canMoveDown = (nextTask: HierarchicalTask): boolean => {
  if (isNull(nextTask)) {
    return false;
  }
  return true;
};

export const canMoveLeft = (currentTask: HierarchicalTask): boolean => {
  if (currentTask.parent !== 0) {
    return true;
  }
  return false;
};

export const canMoveRight = (
  currentTask: HierarchicalTask,
  previousTask: HierarchicalTask,
): boolean => {
  if (currentTask.type === TASK_TYPE_MILESTONE) {
    return false;
  }

  // ONLY WHEN PREVIOUS SIBLING IS AT SAME LEVEL
  if (isNull(previousTask)) {
    return false;
  }
  if (previousTask.parent === currentTask.parent) {
    return true;
  }
  return false;
};

export const getDurationMinutes = (duration: Duration) => {
  if (isNull(duration)) {
    return 0;
  }
  const hours = duration.days * 24 + duration.hours;
  const minutes = hours * 60 + duration.minutes;
  return minutes;
};
